import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import './Coin.css';
import CoinChart from './CoinChart';
import {
  FaShoppingCart,
  FaExchangeAlt,
  FaShareAlt,
  FaWhatsapp,
  FaTwitter,
  FaFacebook,
  FaLinkedin,
} from 'react-icons/fa';
import { useAuth } from '../../Context/AuthContext';
import { addToWatchlist, removeFromWatchlist } from '../../Firebase';
import Faq from './Faq';
import RelatedCoins from './RelatedCoins';
import { ClipLoader } from 'react-spinners';

const Coin = () => {
  const { coinId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [coin, setCoin] = useState(null);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    const fetchCoin = async () => {
      try {
        const response = await axios.get(`https://api.coingecko.com/api/v3/coins/${coinId}`);
        setCoin(response.data);
      } catch (error) {
        console.error('Error fetching coin data:', error);
        setCoin(null);
      }
    };
    fetchCoin();
  }, [coinId]);

  const isWatching = watchlist.includes(coinId);

  const handleAddToWatchlist = async () => {
    if (!currentUser) return navigate('/login');
    try {
      await addToWatchlist(currentUser.uid, coinId);
      setWatchlist(prev => [...prev, coinId]);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    }
  };

  const handleRemoveFromWatchlist = async () => {
    if (!currentUser) return navigate('/login');
    try {
      await removeFromWatchlist(currentUser.uid, coinId);
      setWatchlist(prev => prev.filter(id => id !== coinId));
    } catch (error) {
      console.error('Error removing from watchlist:', error);
    }
  };

  const handleWatchlistToggle = () => {
    if (isWatching) {
      handleRemoveFromWatchlist();
    } else {
      handleAddToWatchlist();
    }
  };

  const shareCoin = () => {
    if (!coin) return;
    const shareData = {
      title: coin.name,
      text: `Check out the latest details about ${coin.name}!`,
      url: window.location.href,
    };
    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else {
      navigator.clipboard
        .writeText(`${shareData.text} ${shareData.url}`)
        .then(() => alert('Link copied to clipboard'))
        .catch(console.error);
    }
  };

  const buyCoin = () => navigate('/dashboard/tables');
  const convertCrypto = () => navigate('/dashboard/profile');

  if (!coin) {
    return (
      <div className="flex justify-center items-center p-10 min-h-[50vh]">
        <ClipLoader />
      </div>
    );
  }

  const shareUrl = encodeURIComponent(window.location.href);
  const encodedMsg = encodeURIComponent(`Check out the price of ${coin.name || 'this coin'}!`);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src={coin.image?.small} alt={coin.name} className="w-8 h-8 sm:w-10 sm:h-10" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{coin.name}</h1>
            <p className="uppercase text-gray-500 text-xs sm:text-sm">{coin.symbol} / USD</p>
          </div>
        </div>
        <div className="flex gap-2 sm:gap-3">
<button
  onClick={isWatching ? handleRemoveFromWatchlist : handleAddToWatchlist}
  className={`${
    isWatching
      ? 'bg-red-600 hover:bg-red-700'
      : 'bg-green-600 hover:bg-green-700'
  } text-white px-4 py-2 rounded-full text-sm transition`}
>
  {isWatching ? 'Remove from Watchlist' : 'Add to Watchlist'}
</button>


          <button
            onClick={shareCoin}
            className="p-2 sm:p-3 rounded-full bg-gray-700 text-white hover:bg-gray-800"
            title="Share"
          >
            <FaShareAlt className="w-4 h-4" />
          </button>
          <button
            onClick={buyCoin}
            className="p-2 sm:p-3 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
            title="Buy"
          >
            <FaShoppingCart className="w-4 h-4" />
          </button>
          <button
            onClick={convertCrypto}
            className="p-2 sm:p-3 rounded-full bg-indigo-600 text-white hover:bg-indigo-700"
            title="Convert"
          >
            <FaExchangeAlt className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="text-3xl font-semibold text-green-600 text-center sm:text-left mt-4">
        ${coin.market_data?.current_price?.usd?.toLocaleString()}
      </div>

      <div className="overflow-x-auto mt-6">
        <table className="min-w-full text-sm text-center border rounded-lg">
          <thead className="bg-gray-100">
            <tr>
              {['1h', '24h', '7d', '14d', '30d', '1yr'].map(label => (
                <th key={label} className="p-2">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {[coin.market_data?.price_change_percentage_1h_in_currency?.usd,
                coin.market_data?.price_change_percentage_24h_in_currency?.usd,
                coin.market_data?.price_change_percentage_7d_in_currency?.usd,
                coin.market_data?.price_change_percentage_14d_in_currency?.usd,
                coin.market_data?.price_change_percentage_30d_in_currency?.usd,
                coin.market_data?.price_change_percentage_1y_in_currency?.usd,
              ].map((val, idx) => (
                <td key={idx} className="p-2">{val?.toFixed(1)}%</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mt-6">
        <div className="p-4 rounded shadow space-y-1">
          <p><strong>24H Low:</strong> ${coin.market_data?.low_24h?.usd?.toLocaleString()}</p>
          <p><strong>24H High:</strong> ${coin.market_data?.high_24h?.usd?.toLocaleString()}</p>
          <p><strong>Market Cap:</strong> ${coin.market_data?.market_cap?.usd?.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded shadow space-y-1">
          <p><strong>Circulating Supply:</strong> {coin.market_data?.circulating_supply?.toLocaleString()}</p>
          <p><strong>Total Supply:</strong> {coin.market_data?.total_supply?.toLocaleString() || 'N/A'}</p>
          <p><strong>All-Time High:</strong> ${coin.market_data?.ath?.usd?.toLocaleString() || 'N/A'}</p>
          <p><strong>All-Time Low:</strong> ${coin.market_data?.atl?.usd?.toLocaleString() || 'N/A'}</p>
        </div>
      </div>

      <div className="mt-6">
        <CoinChart coinId={coinId} />
      </div>

      <div className="flex gap-2 mt-6 flex-wrap justify-start">
        <a href={`https://api.whatsapp.com/send?text=${encodedMsg}%20${shareUrl}`} target="_blank" rel="noreferrer" className="p-2 bg-green-500 rounded-full text-white hover:bg-green-600" title="WhatsApp">
          <FaWhatsapp className="w-4 h-4 sm:w-5 sm:h-5" />
        </a>
        <a href={`https://twitter.com/intent/tweet?text=${encodedMsg}&url=${shareUrl}`} target="_blank" rel="noreferrer" className="p-2 bg-blue-500 rounded-full text-white hover:bg-blue-600" title="Twitter">
          <FaTwitter className="w-4 h-4 sm:w-5 sm:h-5" />
        </a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noreferrer" className="p-2 bg-blue-700 rounded-full text-white hover:bg-blue-800" title="Facebook">
          <FaFacebook className="w-4 h-4 sm:w-5 sm:h-5" />
        </a>
        <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${encodedMsg}`} target="_blank" rel="noreferrer" className="p-2 bg-blue-800 rounded-full text-white hover:bg-blue-900" title="LinkedIn">
          <FaLinkedin className="w-4 h-4 sm:w-5 sm:h-5" />
        </a>
      </div>

      <div className="mt-6">
        <h2 className="text-xl text-white font-bold mb-2">About {coin.name}</h2>
        {coin.description?.en && (
          <div className="text-white prose max-w-none" dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(coin.description.en),
          }} />
        )}
      </div>

      <Faq coin={coin} />
      {coin.categories?.length > 0 ? (
        <RelatedCoins categoryName={coin.categories[0]} />
      ) : (
        <div className="my-8">
          <h2 className="text-2xl font-bold mb-4">Related Coins</h2>
          <p>No related coins found for this cryptocurrency.</p>
        </div>
      )}
    </div>
  );
};

export default Coin;


